/**
 * Le LEGHE di qLeagues lette dalle rotte stagione del BFF Limulo — la parte pura.
 *
 * 🔴 Perche' esiste (Luca, I-224 → L-2312 (a), 2026-09-27): la scheda «Rankings» della Hub CR
 * leggeva le stagioni del monolite Laravel. Al suo posto, dietro `VITE_FEATURE_LEGHE_ENABLED`, va
 * la lega del mese di qLeagues (ADR-0164: una lega QBank al mese per gioco e modalita', coi punti
 * che qTournaments annuncia a torneo concluso). Solo staging: la produzione resta Laravel fino al
 * lancio di Limulo (L-2313 (a), L-2217).
 *
 * 📌 Le rotte sono quelle GIA' montate nel BFF (`app-qlash-api/backend/router.js:1563-1600`
 * @qlash-limulo 7e0eae2): `/v1/seasons`, `/v1/seasons/:id/leaderboard`, `/v1/leaderboard/me`, dietro
 * il setting `enableLeaderboards` (`endpoint-catalog.js:458-495`). Con qLeagues installata le serve
 * lei (priorita' 220) nella forma di `serializeSeason`/`serializeEntry`
 * (`qLeagues/limulo-app/backend/vista.js:52-103` @a78ca60).
 * ⚠️ PRIMA dell'accensione cosa rispondano NON e' misurato: un vuoto (se sull'org sono accesi i
 * fallback di app-qlash-services, priorita' 215, Mongo vuoto — Luca li accese il 20/09 [R dossier
 * leghe-orcb]), un 403 `E_QLASH_ENDPOINT_DISABLED` (setting spento) o un 424 senza provider
 * (ADR-0126: «app-qlash-services resta spenta»). La scheda li dice tutti e tre, ognuno col suo nome.
 * ⚠️ E un 503 di qLeagues (QBank giu') al client NON arriva come 503: il kernel conserva solo gli
 * errori 4xx di un provider, e il resto diventa 424 `E_QLASH_PROVIDER_UNAVAILABLE` — o, coi fallback
 * accesi, ricade IN SILENZIO sul Mongo vuoto (KB `services/qleagues/overview.md`, «Il kernel ripiega
 * in silenzio»: per questo all'accensione si spegne `enableQlashSeasonProviders`).
 *
 * 🔴 IL CORPO HTTP E' LA BUSTA DEL BFF, non il payload del provider: `sendOk` risponde
 * `{ ok, organizationId, data, meta }` (`router.js:89-103`) e il payload sta in `data`. Si srotola
 * QUI (`unwrapData`, come ogni endpoint di `limuloApi`). La prima stesura leggeva `items` al primo
 * livello: tutte e tre le query davano `null` con qualunque 200, e nessun test lo vedeva perche'
 * fingeva gli hook (revisione di #458). Le garanzie ora passano dal client vero.
 *
 * ⚠️ GLI ID NON ATTRAVERSANO I SISTEMI (come in `profiles.ts`): la stagione porta il `gameId` che
 * qTournaments scrive nella busta (su staging `1`, dalla moneta `ELO-1-REGULAR` [D]), il catalogo
 * `GET /v1/games` da' `gameId: game.gameId || game.id` (`policy.js:191`), e il selettore della TMA
 * ragiona sul `6` del monolite. ⇒ Il gioco si trova per NOME (`limuloClashRoyaleGame`) e la stagione
 * si confronta con ENTRAMBE le sue chiavi, normalizzate come le normalizza qLeagues (`giochi.js`).
 * Il filtro resta QUI, lato client: passato al server, una chiave sbagliata risponderebbe «nessuna
 * lega» — un vuoto affermato su una domanda posta male. E se ci sono leghe in corso che non si
 * attribuiscono a NESSUN gioco del catalogo, il vuoto non si afferma (`stagioniSenzaGioco`).
 *
 * ⛔ Niente env, niente orologio, niente DOM: chi monta passa i dati (e i test li fingono).
 */
import { unwrapData } from '@/lib/limulo/model';
import type { LimuloGame } from '@/types/limulo';

/** Le rotte, in un punto solo (come `QUEUE_ROUTES` in `code.ts`). */
export const LEGHE_ROUTES = {
  seasons: '/v1/seasons',
  leaderboard: (seasonId: string) => `/v1/seasons/${encodeURIComponent(seasonId)}/leaderboard`,
  me: '/v1/leaderboard/me',
} as const;

/** Il tetto del server per la lista stagioni (`pagina(payload, 25, 100)` in qLeagues). */
export const LEGHE_SEASONS_LIMIT = 100;
/** Le righe della classifica che la scheda mostra (tetto server 100 in qLeagues, 200 in app-qlash-services). */
export const LEGHE_TOP_LIMIT = 50;

/** `serializeSeason` — solo i campi che la scheda usa. */
export interface LegaStagione {
  id: string;
  seasonId: string;
  title: string;
  gameId: string;
  /** `published` | `active` | `completed` (qLeagues: CREATED/BOOTING → published, ACTIVE/ENDING → active, ENDED → completed). */
  status: string;
  startAt: string | null;
  endAt: string | null;
  metadata: { gameModeKey?: string; season?: string; qbankStatus?: string | null; [k: string]: unknown };
}

/** `serializeEntry` — solo i campi che la scheda usa. */
export interface LegaRiga {
  id: string;
  /** Il `sub` in qLeagues, sia nelle righe sia in `/me`: e' la chiave di «e' mia». */
  entityId: string;
  userId: string;
  displayName: string;
  rank: number;
  score: number;
  wins: number;
  losses: number;
  draws: number;
}

/** Il PAYLOAD paginato delle due liste (`data` della busta): `{ items, page, limit, total }`. */
export interface LegaPagina<T> {
  items: T[];
  total: number;
}

/**
 * La risposta di `/v1/leaderboard/me`, con TRE esiti che non si fondono: forma ignota (`null`: non
 * si afferma niente su di me) · `{ riga: null }` (il server ha detto: nessuna posizione) · la riga.
 */
export interface LegaPosizione {
  riga: LegaRiga | null;
}

const testo = (v: unknown): string => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '');
const numero = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const oggetto = (v: unknown): Record<string, unknown> =>
  v !== null && typeof v === 'object' ? (v as Record<string, unknown>) : {};

/** La normalizzazione di qLeagues (`giochi.js:normalizzaGioco`): maiuscolo, non alfanumerici → `-`. */
export function normalizzaGioco(v: unknown): string {
  return testo(v)
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function stagioneOf(raw: unknown): LegaStagione | null {
  const r = oggetto(raw);
  const id = testo(r.id) || testo(r.seasonId);
  if (id === '') return null;
  return {
    id,
    seasonId: testo(r.seasonId) || id,
    title: testo(r.title),
    gameId: testo(r.gameId),
    status: testo(r.status),
    startAt: testo(r.startAt) || null,
    endAt: testo(r.endAt) || null,
    metadata: oggetto(r.metadata) as LegaStagione['metadata'],
  };
}

export function rigaOf(raw: unknown): LegaRiga | null {
  const r = oggetto(raw);
  const entityId = testo(r.entityId);
  const id = testo(r.id) || testo(r.entryId) || entityId;
  if (id === '') return null;
  return {
    id,
    entityId,
    userId: testo(r.userId),
    displayName: testo(r.displayName),
    rank: numero(r.rank),
    score: numero(r.score),
    wins: numero(r.wins),
    losses: numero(r.losses),
    draws: numero(r.draws),
  };
}

/**
 * La busta `{ ok, data: { items, total } }` srotolata. Un corpo senza `data.items` NON e' una lista
 * vuota: e' una forma che non conosciamo, e si dichiara (`null`) invece di affermare «nessuna lega».
 */
export function paginaOf<T>(envelope: unknown, voce: (x: unknown) => T | null): LegaPagina<T> | null {
  const r = oggetto(unwrapData(envelope));
  if (!Array.isArray(r.items)) return null;
  const items = r.items.map(voce).filter((x): x is T => x !== null);
  return { items, total: Number.isFinite(Number(r.total)) ? Number(r.total) : items.length };
}

/**
 * La busta di `/v1/leaderboard/me`. ⚠️ `unwrapData` da solo non basta: rende `null` sia per una busta
 * senza `data` (forma ignota) sia per `data: null` (nessuna posizione), e sono due cose opposte — la
 * seconda si dice («non hai ancora punti»), la prima no.
 */
export function posizioneOf(envelope: unknown): LegaPosizione | null {
  const busta = oggetto(envelope);
  if (!('data' in busta)) return null;
  if (busta.data === null) return { riga: null };
  const riga = rigaOf(busta.data);
  return riga ? { riga } : null;
}

/** Le stagioni di un gioco, per entrambe le sue chiavi. */
function delGioco(seasons: readonly LegaStagione[], game: LimuloGame | null): LegaStagione[] {
  if (!game) return [];
  const chiavi = chiaviDi(game);
  return seasons.filter((s) => chiavi.has(normalizzaGioco(s.gameId)));
}

function chiaviDi(game: LimuloGame): Set<string> {
  return new Set([normalizzaGioco(game.gameId), normalizzaGioco(game.id)].filter((k) => k !== ''));
}

/**
 * Le leghe IN CORSO del gioco, per modalita'.
 *
 * Solo `active`: una `published` e' creata ma non partita (o e' quella del mese dopo), una
 * `completed` e' chiusa — nessuna delle due e' «la lega di adesso».
 */
export function legheInCorso(seasons: readonly LegaStagione[], game: LimuloGame | null): LegaStagione[] {
  return delGioco(seasons, game)
    .filter((s) => s.status === 'active')
    .sort((a, b) => testo(a.metadata.gameModeKey).localeCompare(testo(b.metadata.gameModeKey)));
}

/**
 * Le leghe del mese CORRENTE che esistono ma non sono partite: `published` col calendario gia'
 * aperto (`startAt <= adesso < endAt`).
 *
 * 🔑 Il caso tipico e' lo START fallito (qleagues-orcr, 27/09 00:34Z): QBank vuole almeno 2 wallet
 * sulla moneta Elo del gioco (`NOT_ENOUGH_PLAYERS`) e la lega resta CREATED ⇒ `published`. Ma
 * `published` viene anche da BOOTING, quindi la CAUSA non si dice: si dice solo che non e' partita.
 * La `published` del mese DOPO (creata nelle ultime 24 ore) ha `startAt` nel futuro e resta fuori;
 * una `published` di un mese passato mai partita ha `endAt` nel passato e resta fuori anch'essa.
 */
export function legheInAttesa(
  seasons: readonly LegaStagione[],
  game: LimuloGame | null,
  adesso: number,
): LegaStagione[] {
  return delGioco(seasons, game).filter((s) => {
    if (s.status !== 'published') return false;
    const inizio = s.startAt ? Date.parse(s.startAt) : NaN;
    const fine = s.endAt ? Date.parse(s.endAt) : NaN;
    return Number.isFinite(inizio) && Number.isFinite(fine) && inizio <= adesso && adesso < fine;
  });
}

/**
 * Le leghe in corso che non appartengono a NESSUN gioco del catalogo.
 *
 * Se ce ne sono, «nessuna lega per Clash Royale» non si puo' affermare: la sua potrebbe essere una di
 * queste, con una chiave che non combacia (il `gameId` della busta contro quello del catalogo non e'
 * misurato). E' la salvaguardia della revisione di #458.
 */
export function stagioniSenzaGioco(seasons: readonly LegaStagione[], games: readonly LimuloGame[]): LegaStagione[] {
  const tutte = new Set(games.flatMap((g) => [...chiaviDi(g)]));
  return seasons.filter((s) => s.status === 'active' && !tutte.has(normalizzaGioco(s.gameId)));
}

/**
 * Il MESE della lega, `AAAA-MM`, come lo scrive qLeagues in `metadata.season`: indipendente dal fuso
 * in cui qLeagues taglia i mesi (`monthBoundaryTz`). Da `startAt` solo se manca — e allora in UTC,
 * che e' il default di qLeagues (ADR-0164 §3).
 */
export function meseDellaLega(lega: LegaStagione): { anno: number; mese: number } | null {
  const m = /^(\d{4})-(\d{2})$/.exec(testo(lega.metadata.season));
  if (m) return { anno: Number(m[1]), mese: Number(m[2]) };
  const d = lega.startAt ? new Date(lega.startAt) : null;
  return d && !Number.isNaN(d.getTime()) ? { anno: d.getUTCFullYear(), mese: d.getUTCMonth() + 1 } : null;
}

/**
 * L'ultimo istante della lega. qLeagues la costruisce su `[inizio, fine)`: `endAt` e' la mezzanotte
 * del primo del mese DOPO, che alla lega non appartiene ⇒ l'ultimo giorno e' quello di `endAt - 1 ms`.
 */
export function ultimoIstante(lega: LegaStagione): number | null {
  const fine = lega.endAt ? Date.parse(lega.endAt) : NaN;
  return Number.isFinite(fine) ? fine - 1 : null;
}
